import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { defer } from 'rxjs';
import { REQUEST, RESPONSE } from 'src/express.tokens';
import { routes, SupportedLang } from 'utils/routes';
import { LanguageService, SUPPORTED_LANGS } from './_shared/services/language/language/language.service';

interface GuardDeps {
  router: Router;
  platformId: object;
  translateService: ITranslationService;
  languageService: LanguageService;
  response?: any;
  request?: any;
}

export const languageGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const platformId = inject(PLATFORM_ID);
  const deps: GuardDeps = {
    router: inject(Router),
    platformId,
    translateService: inject(I18NEXT_SERVICE),
    languageService: inject(LanguageService),
    response: isPlatformServer(platformId) ? inject(RESPONSE) : undefined,
    request: isPlatformServer(platformId) ? inject(REQUEST) : undefined,
  };

  return defer(() => runLanguageGuard(route, state, deps));
};

const runLanguageGuard = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot, deps: GuardDeps) => {
  const { router, platformId, translateService, languageService, response, request } = deps;

  const withDefault = (lang?: string): SupportedLang => {
    const fav = isPlatformBrowser(platformId) && localStorage.getItem('sharryFavouriteLanguage');
    const browserLang = isPlatformBrowser(platformId) ? navigator.language.split('-')[0] : undefined;
    return (lang || fav || browserLang || 'it') as SupportedLang;
  };

  const routeLanguage = withDefault(route.params['lang']);

  // ora aspettiamo che il caricamento risorse (se necessario) sia completo
  await languageService.checkAndPrompt(routeLanguage);

  if (translateService.language !== routeLanguage) {
    try {
      await translateService.changeLanguage(routeLanguage);
    } catch {
      // catch errore
    }
  }

  if (!state.url || state.url === '/') {
    if (isPlatformServer(platformId)) {
      const reqLang = withDefault(request?.i18n?.language);
      response?.redirect(301, '/' + reqLang);
      return router.parseUrl(`/${reqLang}/${routes[reqLang]['routes.explore']}`);
    }
    const lang: SupportedLang = Capacitor.isNativePlatform()
      ? withDefault((await Device.getLanguageCode()).value)
      : (translateService.language as SupportedLang);
    return router.parseUrl(`/${lang}/${routes[lang]['routes.explore']}`);
  }

  if (SUPPORTED_LANGS.map(l => `/${l}`).includes(state.url)) {
    if (isPlatformServer(platformId)) {
      response?.redirect(301, `/${routeLanguage}/${routes[routeLanguage]['routes.explore']}`);
    }
    return router.parseUrl(`/${routeLanguage}/${routes[routeLanguage]['routes.explore']}`);
  }

  if (SUPPORTED_LANGS.includes(routeLanguage)) {
    return true;
  }

  return router.parseUrl('/not-found');
};
